import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/App";
import { Download } from "lucide-react";

export const InstallPrompt = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [show, setShow] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSGuide, setShowIOSGuide] = useState(false);
  const [leaving, setLeaving] = useState(false);

  const isConversationPage = location.pathname.startsWith("/chat/");

  useEffect(() => {
    if (!user) { setShow(false); return; }

    const isStandalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone;
    if (isStandalone) return;

    const dismissCount = parseInt(localStorage.getItem("moji_install_dismiss_count") || "0");
    // Show up to 5 times, with increasing cooldown: 1 day, 2 days, 3 days, 5 days, 7 days
    const cooldowns = [86400000, 172800000, 259200000, 432000000, 604800000];
    if (dismissCount >= 5) return;
    const lastDismissed = parseInt(localStorage.getItem("moji_install_dismissed") || "0");
    const now = Date.now();
    const cooldown = cooldowns[Math.min(dismissCount, cooldowns.length - 1)];
    if (lastDismissed && (now - lastDismissed) < cooldown) return;

    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    setIsIOS(iOS);

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener("beforeinstallprompt", handler);

    // Show after 3 seconds, no auto-dismiss — let user decide
    const showTimer = setTimeout(() => setShow(true), 3000);

    return () => {
      clearTimeout(showTimer);
      window.removeEventListener("beforeinstallprompt", handler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const dismiss = (save = true) => {
    setLeaving(true);
    setTimeout(() => {
      setShow(false);
      setLeaving(false);
      if (save) {
        localStorage.setItem("moji_install_dismissed", Date.now().toString());
        const count = parseInt(localStorage.getItem("moji_install_dismiss_count") || "0");
        localStorage.setItem("moji_install_dismiss_count", String(count + 1));
      }
    }, 300);
  };

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const result = await deferredPrompt.userChoice;
      if (result.outcome === "accepted") {
        dismiss(true);
      }
      setDeferredPrompt(null);
    } else if (isIOS) {
      setShowIOSGuide(true);
    }
    dismiss(true);
  };

  if (!show || isConversationPage) return null;

  return (
    <>
      <div
        className={`fixed bottom-20 left-0 right-0 z-50 flex justify-center px-4 pointer-events-none ${leaving ? 'animate-slide-out-down' : 'animate-slide-up'}`}
        data-testid="install-prompt"
      >
        <div
          className="pointer-events-auto flex items-center gap-3 pl-4 pr-3 py-2.5 rounded-full shadow-lg cursor-pointer active:scale-95 transition-transform"
          style={{
            background: 'rgba(28,36,32,0.9)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            border: '1px solid rgba(118,137,112,0.15)',
          }}
          onClick={handleInstall}
        >
          <Download className="w-3.5 h-3.5 text-[#768970] flex-shrink-0" />
          <span className="text-xs text-white/85 font-medium" style={{ fontFamily: 'Outfit' }}>
            Get the HeyMoji app
          </span>
          <span className="text-[10px] text-white/40 ml-1">free</span>
          <span className="text-[10px] text-white/40 ml-1">·</span>
          <span className="text-[10px] text-white/40" onClick={(e) => { e.stopPropagation(); dismiss(true); }}>
            dismiss
          </span>
        </div>
      </div>

      {/* iOS guide */}
      {showIOSGuide && (
        <div className="fixed inset-0 z-[60] bg-black/60 flex items-end" onClick={() => { setShowIOSGuide(false); dismiss(true); }}>
          <div className="w-full bg-[#1C2420] rounded-t-2xl px-6 pt-5 pb-8 text-white" onClick={(e) => e.stopPropagation()}>
            <div className="w-8 h-0.5 bg-white/20 rounded-full mx-auto mb-5" />
            <p className="text-base font-medium mb-4" style={{ fontFamily: 'Outfit' }}>3 taps to install</p>
            <div className="flex items-center gap-4 mb-3">
              <div className="w-7 h-7 rounded-full bg-[#768970]/20 flex items-center justify-center text-xs font-bold text-[#768970]">1</div>
              <p className="text-sm text-white/70">Tap
                <span className="inline-flex mx-1.5 align-middle">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/></svg>
                </span>
                at the bottom of Safari
              </p>
            </div>
            <div className="flex items-center gap-4 mb-3">
              <div className="w-7 h-7 rounded-full bg-[#768970]/20 flex items-center justify-center text-xs font-bold text-[#768970]">2</div>
              <p className="text-sm text-white/70">Tap <strong className="text-white">"Add to Home Screen"</strong></p>
            </div>
            <div className="flex items-center gap-4 mb-5">
              <div className="w-7 h-7 rounded-full bg-[#768970]/20 flex items-center justify-center text-xs font-bold text-[#768970]">3</div>
              <p className="text-sm text-white/70">Tap <strong className="text-white">"Add"</strong> — done</p>
            </div>
            <button className="w-full py-3 rounded-xl bg-white/5 text-xs text-white/40 tap-scale" onClick={() => { setShowIOSGuide(false); dismiss(true); }}>
              Maybe later
            </button>
          </div>
        </div>
      )}
    </>
  );
};
